<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { throttle } from '../composables/useThrottledResize';

interface Option {
  value: string;
  label: string;
}

interface Props {
  modelValue: string;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Select...',
  disabled: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const isOpen = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);
const menuPosition = ref({ top: 0, left: 0, width: 0 });

const selectedOption = computed(() => {
  return props.options.find(opt => opt.value === props.modelValue);
});

const displayLabel = computed(() => {
  return selectedOption.value?.label || props.placeholder;
});

const updateMenuPosition = () => {
  if (!dropdownRef.value) return;
  const rect = dropdownRef.value.getBoundingClientRect();
  menuPosition.value = {
    top: rect.bottom + window.scrollY + 6,
    left: rect.left + window.scrollX,
    width: rect.width,
  };
};

const toggleDropdown = () => {
  if (props.disabled) return;
  if (!isOpen.value) {
    updateMenuPosition();
  }
  isOpen.value = !isOpen.value;
};

const selectOption = (option: Option) => {
  emit('update:modelValue', option.value);
  isOpen.value = false;
};

const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false;
  }
};

const handleResize = throttle(() => {
  if (isOpen.value) {
    updateMenuPosition();
  }
}, 150);

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('resize', handleResize);
  window.addEventListener('scroll', handleResize, true);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('scroll', handleResize, true);
});

// Close dropdown when value changes
watch(
  () => props.modelValue,
  () => {
    isOpen.value = false;
  }
);
</script>

<template>
  <div
    ref="dropdownRef"
    class="skeuo-dropdown"
    :class="{ 'is-open': isOpen, 'is-disabled': disabled }"
  >
    <!-- Main trigger button -->
    <button
      type="button"
      class="skeuo-dropdown-trigger"
      @click="toggleDropdown"
      :disabled="disabled"
      :aria-expanded="isOpen"
    >
      <span class="skeuo-dropdown-text">{{ displayLabel }}</span>
      <span class="skeuo-dropdown-arrow" :class="{ 'is-open': isOpen }">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </span>
    </button>

    <!-- Dropdown menu -->
    <Teleport to="body">
      <Transition name="dropdown">
        <div
          v-show="isOpen"
          class="skeuo-dropdown-menu"
          :style="{
            top: menuPosition.top + 'px',
            left: menuPosition.left + 'px',
            width: menuPosition.width + 'px',
          }"
        >
          <div class="skeuo-dropdown-options">
            <button
              v-for="option in options"
              :key="option.value"
              type="button"
              class="skeuo-dropdown-option"
              :class="{ 'is-selected': option.value === modelValue }"
              @click="selectOption(option)"
            >
              <span class="skeuo-option-text">{{ option.label }}</span>
              <span v-if="option.value === modelValue" class="skeuo-checkmark">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </span>
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
/* Skeuomorphic Dropdown Component */
.skeuo-dropdown {
  position: relative;
  display: inline-block;
  width: 100%;
  max-width: 280px;
}

/* Trigger button - looks like a paper card with depth */
.skeuo-dropdown-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  background: linear-gradient(180deg, #ffffff 0%, #f5f5f0 100%);
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow:
    0 2px 0 #e5e7eb,
    0 3px 6px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  font-family: inherit;
  font-size: 0.95rem;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
}

.skeuo-dropdown-trigger:hover:not(:disabled) {
  background: linear-gradient(180deg, #ffffff 0%, #fafaf5 100%);
  box-shadow:
    0 2px 0 #e5e7eb,
    0 4px 8px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  transform: translateY(-1px);
}

.skeuo-dropdown-trigger:active:not(:disabled) {
  transform: translateY(1px);
  box-shadow:
    0 1px 0 #e5e7eb,
    0 2px 4px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.skeuo-dropdown.is-open .skeuo-dropdown-trigger {
  border-color: #9ca3af;
  box-shadow:
    0 1px 0 #d1d5db,
    0 2px 4px rgba(0, 0, 0, 0.06),
    inset 0 2px 4px rgba(0, 0, 0, 0.04);
  transform: translateY(1px);
}

.skeuo-dropdown-trigger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: linear-gradient(180deg, #f3f4f6 0%, #e5e7eb 100%);
}

.skeuo-dropdown-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Arrow icon */
.skeuo-dropdown-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: transform 0.2s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  flex-shrink: 0;
}

.skeuo-dropdown-arrow.is-open {
  transform: rotate(180deg);
}

/* Dropdown menu - appears like a notepad */
.skeuo-dropdown-menu {
  position: fixed;
  z-index: 9999;
  background: linear-gradient(180deg, #ffffff 0%, #fafaf5 100%);
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow:
    0 4px 0 #e5e7eb,
    0 8px 20px rgba(0, 0, 0, 0.15),
    0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transform-origin: top center;
}

/* Paper texture overlay for menu - simplified */
.skeuo-dropdown-menu::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Crect fill='%23000' opacity='0.02' width='100%25' height='100%25'/%3E%3Ccircle cx='25' cy='25' r='0.5' fill='%23000' opacity='0.03'/%3E%3Ccircle cx='75' cy='40' r='0.5' fill='%23000' opacity='0.02'/%3E%3Ccircle cx='40' cy='70' r='0.5' fill='%23000' opacity='0.03'/%3E%3C/svg%3E");
  opacity: 0.15;
  pointer-events: none;
  border-radius: inherit;
}

/* Options container */
.skeuo-dropdown-options {
  position: relative;
  max-height: 240px;
  overflow-y: auto;
  padding: 0.375rem;
}

/* Individual option - notebook style */
.skeuo-dropdown-option {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  background: transparent;
  border: none;
  border-radius: 4px;
  font-family: inherit;
  font-size: 0.95rem;
  color: #374151;
  cursor: pointer;
  transition: all 0.12s ease;
  text-align: left;
  position: relative;
}

.skeuo-dropdown-option:hover {
  background: linear-gradient(180deg, #fef3c7 0%, #fde68a 100%);
  color: #1f2937;
}

.skeuo-dropdown-option.is-selected {
  background: linear-gradient(180deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  font-weight: 500;
}

.skeuo-dropdown-option.is-selected:hover {
  background: linear-gradient(180deg, #bfdbfe 0%, #93c5fd 100%);
}

.skeuo-option-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Checkmark for selected option */
.skeuo-checkmark {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  flex-shrink: 0;
}

/* Scrollbar styling for options */
.skeuo-dropdown-options::-webkit-scrollbar {
  width: 6px;
}

.skeuo-dropdown-options::-webkit-scrollbar-track {
  background: transparent;
}

.skeuo-dropdown-options::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.skeuo-dropdown-options::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Dropdown animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.96);
}

.dropdown-enter-to,
.dropdown-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}
</style>
